import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Database, FileText, RefreshCw, Save } from 'lucide-react';
import adminBigQueryHandoffService, {
  BigQueryExportManifest,
  BigQueryHandoffContract,
  BigQueryHandoffPayload,
  BigQueryHandoffSetting,
} from '../../services/api/adminBigQueryHandoffService';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyInput,
  TaskyLabel,
  TaskySpinner,
} from '../tasky';

const defaultForm: BigQueryHandoffPayload = {
  is_enabled: false,
  project_id: '',
  location: 'asia-northeast1',
  raw_dataset: 'raw',
  staging_dataset: 'staging',
  mart_dataset: 'mart',
  semantic_dataset: 'semantic',
  export_directory: 'bigquery-handoff',
};

const BigQueryHandoffPanel: React.FC = () => {
  const [setting, setSetting] = useState<BigQueryHandoffSetting | null>(null);
  const [contract, setContract] = useState<BigQueryHandoffContract | null>(null);
  const [form, setForm] = useState<BigQueryHandoffPayload>(defaultForm);
  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [clearServiceAccount, setClearServiceAccount] = useState(false);
  const [exportDate, setExportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [manifest, setManifest] = useState<BigQueryExportManifest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const exportFiles = useMemo(() => {
    const source = manifest ?? setting?.last_export_manifest;
    return source?.files ? Object.entries(source.files) : [];
  }, [manifest, setting]);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminBigQueryHandoffService.get();
      const nextSetting = response.data.setting;
      setSetting(nextSetting);
      setContract(response.data.contract);
      setManifest(nextSetting.last_export_manifest);
      setForm({
        is_enabled: nextSetting.is_enabled,
        project_id: nextSetting.project_id ?? '',
        location: nextSetting.location,
        raw_dataset: nextSetting.raw_dataset,
        staging_dataset: nextSetting.staging_dataset,
        mart_dataset: nextSetting.mart_dataset,
        semantic_dataset: nextSetting.semantic_dataset,
        export_directory: nextSetting.export_directory,
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'BigQuery設定の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (key: keyof BigQueryHandoffPayload, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setMessage(null);
      const payload: BigQueryHandoffPayload = {
        ...form,
        project_id: form.project_id || null,
      };

      if (serviceAccountJson.trim()) {
        payload.service_account_json = serviceAccountJson.trim();
      }
      if (clearServiceAccount) {
        payload.clear_service_account_json = true;
      }

      const response = await adminBigQueryHandoffService.update(payload);
      setSetting(response.data.setting);
      setServiceAccountJson('');
      setClearServiceAccount(false);
      setMessage('BigQuery受け渡し設定を保存しました。');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'BigQuery設定の保存に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setError(null);
      setMessage(null);
      const response = await adminBigQueryHandoffService.export(exportDate);
      setSetting(response.data.setting);
      setManifest(response.data.manifest);
      setMessage('受け渡しファイルを生成しました。');
    } catch (err: any) {
      setError(err?.response?.data?.message || '受け渡しファイルの生成に失敗しました。');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-3 py-10">
        <TaskySpinner size="md" />
        <span className="text-sm text-gray-600">BigQuery設定を読み込み中...</span>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      {message && <TaskyAlert tone="success">{message}</TaskyAlert>}
      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-dark">BigQuery BI連携</h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <TaskyBadge variant={form.is_enabled ? 'success' : 'secondary'}>
              {form.is_enabled ? '有効' : '無効'}
            </TaskyBadge>
            <TaskyBadge variant={setting?.has_service_account_json ? 'success' : 'secondary'}>
              {setting?.has_service_account_json ? '鍵保存済み' : '鍵未設定'}
            </TaskyBadge>
            {setting?.last_export_status && (
              <TaskyBadge variant={setting.last_export_status === 'success' ? 'success' : 'destructive'}>
                最終生成: {setting.last_export_status}
              </TaskyBadge>
            )}
          </div>
        </div>
        <TaskyButton size="sm" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <RefreshCw className="animate-spin" /> : <Save />}
          保存
        </TaskyButton>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
        <div className="rounded-tasky-md border border-warm-border bg-white p-4 sm:p-5">
          <div className="mb-4 flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-dark">接続設定</h3>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex items-center gap-2 rounded-tasky-sm border border-warm-border bg-warm-bg px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_enabled}
                onChange={(event) => updateField('is_enabled', event.target.checked)}
              />
              BI連携を有効にする
            </label>

            <div>
              <TaskyLabel>GCPプロジェクトID</TaskyLabel>
              <TaskyInput
                value={form.project_id ?? ''}
                onChange={(event) => updateField('project_id', event.target.value)}
                placeholder="tasky-growth-bi"
              />
            </div>

            <div>
              <TaskyLabel>ロケーション</TaskyLabel>
              <TaskyInput
                value={form.location}
                onChange={(event) => updateField('location', event.target.value)}
                placeholder="asia-northeast1"
              />
            </div>

            <div>
              <TaskyLabel>出力ディレクトリ</TaskyLabel>
              <TaskyInput
                value={form.export_directory}
                onChange={(event) => updateField('export_directory', event.target.value)}
                placeholder="bigquery-handoff"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <TaskyLabel>raw</TaskyLabel>
              <TaskyInput value={form.raw_dataset} onChange={(event) => updateField('raw_dataset', event.target.value)} />
            </div>
            <div>
              <TaskyLabel>staging</TaskyLabel>
              <TaskyInput value={form.staging_dataset} onChange={(event) => updateField('staging_dataset', event.target.value)} />
            </div>
            <div>
              <TaskyLabel>mart</TaskyLabel>
              <TaskyInput value={form.mart_dataset} onChange={(event) => updateField('mart_dataset', event.target.value)} />
            </div>
            <div>
              <TaskyLabel>semantic</TaskyLabel>
              <TaskyInput value={form.semantic_dataset} onChange={(event) => updateField('semantic_dataset', event.target.value)} />
            </div>
          </div>

          <div className="mt-4">
            <TaskyLabel>サービスアカウントJSON</TaskyLabel>
            <textarea
              value={serviceAccountJson}
              onChange={(event) => setServiceAccountJson(event.target.value)}
              className="mt-1 min-h-[120px] w-full rounded-tasky-sm border border-warm-border bg-white px-3 py-2 font-mono text-xs text-dark outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder={setting?.has_service_account_json ? '保存済みの鍵を変更する場合のみ貼り付け' : '{ "type": "service_account", ... }'}
            />
            {setting?.has_service_account_json && (
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={clearServiceAccount}
                  onChange={(event) => setClearServiceAccount(event.target.checked)}
                />
                保存済みの鍵を削除する
              </label>
            )}
          </div>
        </div>

        <div className="rounded-tasky-md border border-warm-border bg-white p-4 sm:p-5">
          <div className="mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-dark">日次ファイル生成</h3>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex-1">
              <TaskyLabel>対象日</TaskyLabel>
              <TaskyInput
                type="date"
                value={exportDate}
                onChange={(event) => setExportDate(event.target.value)}
              />
            </div>
            <div className="flex items-end">
              <TaskyButton onClick={handleExport} disabled={isExporting}>
                {isExporting ? <RefreshCw className="animate-spin" /> : <Database />}
                生成
              </TaskyButton>
            </div>
          </div>

          <div className="mt-4 rounded-tasky-sm bg-warm-bg p-3 text-xs text-gray-600">
            <div className="flex items-center gap-2 text-dark">
              {setting?.last_export_status === 'success' ? (
                <CheckCircle2 className="h-4 w-4 text-success" />
              ) : (
                <AlertCircle className="h-4 w-4 text-gray-400" />
              )}
              <span className="font-medium">
                {setting?.last_exported_at ? new Date(setting.last_exported_at).toLocaleString('ja-JP') : '未生成'}
              </span>
            </div>
            {setting?.last_export_message && <p className="mt-2">{setting.last_export_message}</p>}
          </div>

          {exportFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              {exportFiles.map(([name, file]) => (
                <div key={name} className="flex items-center justify-between gap-3 rounded-tasky-sm border border-warm-border px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <p className="font-medium text-dark">{name}</p>
                    <p className="truncate text-gray-500">{file.path}</p>
                  </div>
                  <TaskyBadge variant="warm">{file.rows.toLocaleString()} rows</TaskyBadge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {contract && (
        <div className="rounded-tasky-md border border-warm-border bg-white p-4 sm:p-5">
          <h3 className="mb-3 text-sm font-semibold text-dark">受け渡し契約</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {Object.entries(contract.files).map(([file, table]) => (
              <div key={file} className="flex items-center justify-between gap-3 rounded-tasky-sm bg-warm-bg px-3 py-2 text-xs">
                <span className="font-mono text-dark">{file}</span>
                <span className="text-gray-500">{table}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default BigQueryHandoffPanel;
